import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "../styles/WelcomeTour.css";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function WelcomeTour({
  open,
  companyName,
  planName,
  isPilot, // ✅ new
  onJumpTab,
  onOpenSidebar,
  onClose,
  onDontShowAgain,
}) {
  const [step, setStep] = useState(0);
  const [spot, setSpot] = useState(null); // {left,top,width,height,radius}
  const [dontShow, setDontShow] = useState(false);

  const cardRef = useRef(null);
  const lastActiveEl = useRef(null);
  const ranOnEnterForStep = useRef(null); // prevents loops

  const steps = useMemo(
    () => [
      {
        id: "intro",
        title: `Welcome to Oiiko${companyName ? `, ${companyName}` : ""}`,
        desc:
          "This is your command center. In a minute, you’ll know where the key tools live and what to do first.",
        type: "modal",
        illustration: "rocket",
      },

      ...(isPilot
        ? [
            {
              id: "pilot",
              title: "Pilot Program Mode",
              desc:
                "You’re in a Pilot Program — you have full access while we validate fit and performance. We’ll use your feedback to improve the platform before you subscribe.",
              type: "modal",
              illustration: "check",
            },
          ]
        : []),

      {
        id: "notif",
        title: "Notifications",
        desc:
          "Important activity and alerts show up here so nothing critical happens silently. Click the bell to review updates.",
        type: "spot",
        target: "#nav-notifications-btn",
        illustration: "bell",
      },
      {
        id: "whatsnew",
        title: "What’s New",
        desc:
          "Platform updates and release notes show up here. Click the Sparkles icon to see new features and improvements.",
        type: "spot",
        target: "#nav-whatsnew-btn",
        illustration: "sparkles",
      },

      {
        id: "search",
        title: "Global Search (⌘K / Ctrl+K)",
        desc:
          "Jump anywhere fast. Press ⌘K (Mac) or Ctrl+K (Windows) to search and open pages instantly.",
        type: "spot",
        // ✅ BEST: give your search button an id="nav-search-btn"
        // If you don’t add an id, you can use aria-label selector below.
        target: '#nav-search-btn, button[aria-label="Search"]',
        illustration: "search",
      },

      {
        id: "Account settings",
        title: "Account profile, Settings & Billing",
        desc:
          "Manage your company account profile, settings, notifications, time zone, and subscription from here.",
        type: "spot",
        // ✅ Your settings button already has aria-label="Settings & billing"
        target: "#nav-profile-btn",
        illustration: "gear",
      },

      {
        id: "sidebar",
        title: "Main Navigation",
        desc:
          "Use the sidebar to move between features like Employees, Time Tracking, Scheduling, and more.",
        type: "spot",
        target: "#sidebar-toggle-btn",
        onEnter: async () => {
          await Promise.resolve(onOpenSidebar?.());
          await wait(120);
        },
        illustration: "sidebar",
      },

      {
        id: "help",
        title: "Help Center",
        desc:
          "Every module has step-by-step guides and quick tips. If you’re ever stuck, start here.",
        type: "spot",
        // ✅ BEST: add id="nav-help-center" to the Help Center nav item button
        // Fallback: the selector below *may* match other buttons, so id is recommended.
        target: "#nav-help-center",
        illustration: "help",
      },

      {
        id: "quick",
        title: "Your First Wins",
        desc:
          "Complete Quick Setup to get your first employee added and notifications configured then you’re ready to run.",
        type: "action",
        actionLabel: "Go to Quick Setup",
        onAction: () => onJumpTab?.("quick-guide"),
        illustration: "check",
      },
    ],
    [companyName, onJumpTab, isPilot, onOpenSidebar]
  );

  const placeSpotlight = useCallback(async () => {
    const s = steps[step];
    if (!s || s.type !== "spot" || !s.target) {
      setSpot(null);
      return;
    }
    let el = null;
    for (let i = 0; i < 8; i++) {
      el = document.querySelector(s.target);
      if (el) break;
      await wait(50);
      if (steps[step] !== s) return;
    }
    if (!el) {
      setSpot(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = 8;
    const radius = Math.min(14, Math.max(8, Math.min(r.width, r.height) / 4));
    setSpot({
      left: Math.max(0, r.left - pad + window.scrollX),
      top: Math.max(0, r.top - pad + window.scrollY),
      width: r.width + pad * 2,
      height: r.height + pad * 2,
      radius,
    });
  }, [step, steps]);

  useEffect(() => {
    if (!open) return;
    const s = steps[step];
    (async () => {
      if (
        typeof s?.onEnter === "function" &&
        ranOnEnterForStep.current !== s.id
      ) {
        ranOnEnterForStep.current = s.id;
        await s.onEnter();
      }
      await placeSpotlight();
    })();

    const handler = () => placeSpotlight();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, step, steps, placeSpotlight]);

  useEffect(() => {
    if (open) {
      lastActiveEl.current = document.activeElement;
      setTimeout(() => cardRef.current?.focus(), 0);
      const onKey = (e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          next();
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          prev();
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    } else {
      setStep(0);
      setSpot(null);
      ranOnEnterForStep.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    onDontShowAgain?.(!!dontShow);
    onClose?.();
    if (lastActiveEl.current?.focus)
      setTimeout(() => lastActiveEl.current.focus(), 0);
  };

  const next = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!open) return null;

  const cur = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;

  return (
    <div className="wt-overlay" role="dialog" aria-modal="true">
      <div className="wt-backdrop" aria-hidden="true" />
      {spot && (
        <div
          className="wt-spotlight"
          style={{
            left: `${spot.left}px`,
            top: `${spot.top}px`,
            width: `${spot.width}px`,
            height: `${spot.height}px`,
            borderRadius: `${spot.radius}px`,
          }}
          aria-hidden="true"
        />
      )}

      <section className="wt-card" ref={cardRef} tabIndex={-1}>
        <header className="wt-head">
          <div className="wt-brand">
            <div className="wt-logo" aria-hidden="true">
              <span />
            </div>
            <div className="wt-meta">
              <h2 className="wt-title">Platform Tour</h2>
              {planName && <div className="wt-plan-badge">{planName}</div>}
            </div>
          </div>

          <button
            className="wt-icon-btn"
            aria-label="Close tour"
            onClick={handleClose}
          >
            ×
          </button>
        </header>

        <div className="wt-body">
          <div className="wt-illustration" aria-hidden="true">
            {cur.illustration && <Illustration type={cur.illustration} />}
          </div>
          <div className="wt-copy">
            <div className="wt-step-eyebrow">
              Step {step + 1} <span>of {steps.length}</span>
            </div>
            <h3 className="wt-step-title">{cur.title}</h3>
            <p className="wt-step-desc">{cur.desc}</p>
          </div>
        </div>

        <footer className="wt-foot">
          <div className="wt-progress">
            {steps.map((_, i) => (
              <button
                key={i}
                className={`wt-dot ${i === step ? "active" : ""}`}
                aria-label={`Go to step ${i + 1}`}
                onClick={() => {
                  if (i !== step) setStep(i);
                }}
              />
            ))}
          </div>

          <div className="wt-actions">
            <button className="wt-btn ghost" onClick={handleClose}>
              Skip
            </button>
            {cur.type === "action" ? (
              <button
                className="wt-btn primary"
                onClick={() => {
                  cur.onAction?.();
                  handleClose();
                }}
              >
                {cur.actionLabel || "Finish"}
              </button>
            ) : (
              <>
                <button
                  className="wt-btn"
                  onClick={prev}
                  disabled={isFirst}
                  aria-disabled={isFirst}
                >
                  Back
                </button>
                <button className="wt-btn primary" onClick={next}>
                  {isLast ? "Finish" : "Next"}
                </button>
              </>
            )}
          </div>
        </footer>
        <div className="wt-opt-container">
          {/* moved here: compact option */}
          <label
            className="wt-opt"
            title="Don’t show this tour automatically again"
          >
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
            />
            Don’t show again
          </label>
        </div>
      </section>
    </div>
  );
}

/** inline, asset-free illustrations */
function Illustration({ type }) {
  switch (type) {
    case "rocket":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M38 6c9 3 14 10 14 20 0 6-2 10-6 14l-9 9-7 7-6-6 7-7 9-9c4-4 6-8 6-14 0-5-2-9-8-14z"
            fill="currentColor"
            opacity="0.15"
          />
          <circle cx="36" cy="18" r="4" fill="currentColor" />
        </svg>
      );
    case "bell":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M12 44h40l-4-6V28a16 16 0 10-32 0v10l-4 6z"
            fill="currentColor"
            opacity="0.15"
          />
          <circle cx="32" cy="52" r="4" fill="currentColor" />
        </svg>
      );
    case "sparkles":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <path
            d="M32 10l3 10 10 3-10 3-3 10-3-10-10-3 10-3 3-10z"
            fill="currentColor"
            opacity="0.18"
          />
          <path
            d="M48 30l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z"
            fill="currentColor"
            opacity="0.22"
          />
          <path
            d="M18 34l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z"
            fill="currentColor"
            opacity="0.16"
          />
        </svg>
      );

    case "search":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle
            cx="28"
            cy="28"
            r="14"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            opacity="0.35"
          />
          <line
            x1="38"
            y1="38"
            x2="54"
            y2="54"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.35"
          />
        </svg>
      );
    case "gear":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="32" r="8" fill="currentColor" opacity="0.25" />
          <g opacity="0.2">
            <rect x="30" y="4" width="4" height="10" />
            <rect x="30" y="50" width="4" height="10" />
            <rect x="4" y="30" width="10" height="4" />
            <rect x="50" y="30" width="10" height="4" />
            <rect
              x="46"
              y="10"
              width="4"
              height="10"
              transform="rotate(45 48 15)"
            />
            <rect
              x="14"
              y="44"
              width="4"
              height="10"
              transform="rotate(45 16 49)"
            />
            <rect
              x="10"
              y="10"
              width="4"
              height="10"
              transform="rotate(-45 12 15)"
            />
            <rect
              x="42"
              y="44"
              width="4"
              height="10"
              transform="rotate(-45 44 49)"
            />
          </g>
        </svg>
      );

    case "user":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="22" r="10" fill="currentColor" opacity="0.2" />
          <rect
            x="16"
            y="36"
            width="32"
            height="16"
            rx="8"
            fill="currentColor"
            opacity="0.15"
          />
        </svg>
      );
    case "sidebar":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <rect
            x="8"
            y="12"
            width="16"
            height="40"
            rx="4"
            fill="currentColor"
            opacity="0.2"
          />
          <rect
            x="28"
            y="12"
            width="28"
            height="40"
            rx="6"
            fill="currentColor"
            opacity="0.12"
          />
        </svg>
      );
    case "help":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.12" />
          <path
            d="M26 24a6 6 0 0112 0c0 4-6 5-6 9"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            opacity="0.6"
          />
          <circle cx="32" cy="44" r="2.5" fill="currentColor" />
        </svg>
      );

    case "check":
      return (
        <svg viewBox="0 0 64 64" className="illus">
          <circle cx="32" cy="32" r="22" fill="currentColor" opacity="0.12" />
          <path
            d="M22 33l6 6 14-14"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
}
